import {
  Avatar,
  Box,
  Container,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import KongSwapTitle from '@assets/icons/kongswap-title.png'
import ICPSwapTitle from '@assets/icons/icpswap-title.svg'
import OdinTitle from '@assets/icons/odin-title.svg'
import { BsArrowLeftRight } from 'react-icons/bs'
import { formatNumber } from '@plug-core/utils/formatNumber'
import { SwapProvider } from '@plug-core/interfaces/swapInterface'
import { Swap } from '@plug-core/interfaces/transactionActivity'
import { PlugToken } from '@plug-core/interfaces/common'

interface SwapListItemProps {
  label: string
  value?: string
  divider?: boolean
}

const SwapListItem: React.FC<SwapListItemProps> = ({ label, value, divider = true }) => {
  return (
    <ListItem divider={divider}>
      <ListItemText primary={label} />
      <ListItemText sx={{ textAlign: 'right' }} secondary={value} />
    </ListItem>
  )
}

const SWAP_PROVIDER_TITLE_MAP = {
  [SwapProvider.ICPSWAP]: ICPSwapTitle,
  [SwapProvider.KONGSWAP]: KongSwapTitle,
  [SwapProvider.ODIN]: OdinTitle,
}

const SWAP_PROVIDER_GRADIENT_MAP = {
  [SwapProvider.ICPSWAP]: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
  [SwapProvider.KONGSWAP]: 'linear-gradient(135deg, #004d40 0%, #00251a 100%)',
  [SwapProvider.ODIN]: 'linear-gradient(135deg, rgb(0 99 99) 0%, rgb(0 49 49) 60%)',
}

export const SwapDetails = () => {
  const { t } = useTranslation()
  const location = useLocation()

  const swapDetails: Swap = location.state?.swapDetails
  const fromToken: PlugToken = location.state?.fromToken
  const toToken: PlugToken = location.state?.toToken

  return (
    <Container maxWidth="sm">
      <Stack spacing={2} mb={2}>
        <Paper
          elevation={0}
          sx={{
            background: SWAP_PROVIDER_GRADIENT_MAP[swapDetails.provider],
            borderRadius: '16px',
            p: 2,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="center">
            <Box
              component="img"
              src={SWAP_PROVIDER_TITLE_MAP[swapDetails.provider]}
              sx={{
                height: '40px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                },
              }}
              alt={swapDetails.provider}
            />
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: '1px solid rgba(255,255,255,0.1)',
            p: { xs: 2, sm: 3 },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            px={2}
          >
            <Stack alignItems="center" spacing={1} width="30%">
              <Avatar
                sx={{
                  width: { xs: 48, sm: 60 },
                  height: { xs: 48, sm: 60 },
                  border: '3px solid rgba(255,255,255,0.1)',
                  background: 'rgba(255,255,255,0.05)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
                src={fromToken.logo}
              >
                {fromToken.symbol}
              </Avatar>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  fontWeight: 500,
                  textAlign: 'center',
                  height: '2.5rem',
                  textOverflow: 'ellipsis',
                  width: '100%',
                }}
              >
                <span style={{ display: 'block' }}>
                  {formatNumber(Number(swapDetails.token0Amount))}
                </span>
                <span style={{ display: 'block' }}>{fromToken.symbol}</span>
              </Typography>
            </Stack>

            <Box width="40%" display="flex" justifyContent="center">
              <BsArrowLeftRight
                style={{
                  fontSize: '2rem',
                  color: 'rgba(255,255,255,0.8)',
                  padding: '0.4rem',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  boxShadow: '0px 4px 10px rgba(0,0,0,0.2)',
                  cursor: 'pointer',
                }}
              />
            </Box>

            <Stack alignItems="center" spacing={1} width="30%">
              <Avatar
                sx={{
                  width: { xs: 48, sm: 60 },
                  height: { xs: 48, sm: 60 },
                  border: '3px solid rgba(76,175,80,0.3)',
                  background: 'rgba(76,175,80,0.05)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
                src={toToken.logo}
              >
                {toToken.symbol}
              </Avatar>
              <Typography
                variant="h6"
                color="success.main"
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  fontWeight: 500,
                  textAlign: 'center',
                  height: '2.5rem',
                  textOverflow: 'ellipsis',
                  width: '100%',
                }}
              >
                <Box>{formatNumber(Number(swapDetails.token1Amount))}</Box>
                <Box>{toToken.symbol}</Box>
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        <List
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: '16px',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
            '& .MuiListItem-root': {
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'rgba(255,255,255,0.05)',
              },
              '&:last-child': {
                borderBottom: 'none',
              },
            },
          }}
        >
          {swapDetails.token0Price && (
            <SwapListItem
              label={`${fromToken.symbol} ${t('swapDetails.price')}`}
              value={formatNumber(swapDetails.token0Price, '$')}
            />
          )}
          {swapDetails.token1Price && (
            <SwapListItem
              label={`${toToken.symbol} ${t('swapDetails.price')}`}
              value={formatNumber(swapDetails.token1Price, '$')}
            />
          )}
          {swapDetails.exchangeRate && (
            <SwapListItem
              label={t('swapDetails.exchangeRate')}
              value={`1 ${fromToken.symbol} = ${formatNumber(Number(swapDetails.exchangeRate), toToken.symbol)}`}
            />
          )}
          {swapDetails.amountUSD && (
            <SwapListItem
              label={`${t('swapDetails.valueUSD')}`}
              value={formatNumber(swapDetails.amountUSD, '$')}
            />
          )}
          {swapDetails.slippage && (
            <SwapListItem
              label={`${t('swapDetails.slippage')}`}
              value={`${swapDetails.slippage}%`}
            />
          )}
        </List>
      </Stack>
    </Container>
  )
}
